import React, { useEffect, useState } from 'react';
import { ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Popover, Tooltip } from 'antd';
import { FiLogOut } from 'react-icons/fi';
import { HiOutlineMenuAlt2 } from 'react-icons/hi';
import { IoChevronForward } from 'react-icons/io5';
import { isEmpty } from 'lodash';
import { LayoutDashboard, Package, ShoppingCart, Search, Truck, FileText, Settings as SettingsIcon, Users } from 'lucide-react';

import { SignOut } from '../../slices/auth.js';

import { PERMISSION_MAP } from '../../constants/routes';

import { SidebarContainer } from './style';
import 'react-pro-sidebar/dist/css/styles.css';

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [locationPathname, setLocationPathname] = useState(location.pathname);

  const { currentUser: user, parentUser, currentActiveSubscription } = useSelector((state) => state?.auth || {});

  let profileUser = user;
  const splitedName = profileUser?.full_name?.toUpperCase()?.split(' ') || [];
  const abbreviatedName = `${splitedName?.[0]?.[0] || ''}${splitedName?.[1]?.[0] || ''}`;

  const iconProps = (isActive) => ({
    size: 18,
    strokeWidth: 1.75,
    color: isActive ? '#1565D8' : '#4E5969'
  });

  useEffect(() => {
    const activeMenu = menuData.find(menu => {
      if (menu.subMenu) {
        return menu.subMenu.some(item => item.path === location.pathname);
      }
      return menu.path === location.pathname;
    });
    if (activeMenu && activeMenu.subMenu) {
      setOpenSubMenu(activeMenu.label);
    } else {
      setOpenSubMenu(null);
    }

    setLocationPathname(location.pathname);
  }, [location.pathname, menuData]);

  const isMenuActive = (paths, submenuLabel) => paths.includes(locationPathname) || openSubMenu === submenuLabel;

  const userMenu = [
    {
      label: 'Dashboard',
      icon: <LayoutDashboard {...iconProps(locationPathname === '/dashboard')} />,
      path: '/dashboard',
      permission: ['dashboard']
    },
    {
      label: 'Inventory',
      permission: ['inventory'],
      icon: <Package {...iconProps(isMenuActive(['/inventory/sku', '/inventory/products', '/inventory/vendor'], 'Inventory'))} />,
      subMenu: [
        { label: 'SKU', path: '/inventory/sku', permission: ['inventory'] },
        { label: 'Products', path: '/inventory/products', permission: ['inventory'] },
        { label: 'Vendor Items', path: '/inventory/vendor', permission: ['inventory'] }
      ]
    },
    {
      label: 'Purchasing',
      permission: ['purchasing'],
      icon: <ShoppingCart {...iconProps(isMenuActive(['/vendors', '/po-history', '/restock'], 'Purchasing'))} />,
      subMenu: [
        { label: 'Vendor CRM', path: '/vendors', permission: ['purchasing'] },
        { label: 'Purchase Orders', path: '/po-history', permission: ['purchasing'] },
        { label: 'Replenishment', path: '/restock', permission: ['purchasing'] }
      ]
    },
    {
      label: 'Sourcing',
      permission: ['catalog'],
      icon: <Search {...iconProps(isMenuActive(['/sourcing', '/database'], 'Sourcing'))} />,
      subMenu: [
        { label: 'Catalog Analyzer', path: '/sourcing', permission: ['catalog'] },
        { label: 'Database', path: '/database', permission: ['catalog'] }
      ]
    },
    {
      label: 'Logistics',
      permission: ['logistics'],
      icon: <Truck {...iconProps(locationPathname.includes('/work-order') || openSubMenu === 'Logistics')} />,
      subMenu: [
        { label: 'Work Orders', path: '/work-orders', permission: ['logistics'] }
      ]
    },
    {
      label: 'File History',
      icon: <FileText {...iconProps(locationPathname === '/file-upload-history')} />,
      path: '/file-upload-history',
      permission: ['fileHistory']
    },
    {
      label: 'Settings',
      icon: <SettingsIcon {...iconProps(isMenuActive(['/account', '/users', '/billing'], 'Settings'))} />,
      permission: ['settings'],
      subMenu: [
        { label: 'Account', path: '/account', permission: ['settings'] },
        { label: 'Employee', path: '/employees', permission: ['settings'] },
        { label: 'Billing', path: '/billing', permission: ['settings'] }
      ]
    }
  ];

  const warehouseMenu = [
    {
      label: 'Dashboard',
      icon: <LayoutDashboard {...iconProps(locationPathname === '/dashboard')} />,
      path: '/dashboard',
      permission: ['dashboard']
    },
    {
      label: 'Logistics',
      icon: <Truck {...iconProps(isMenuActive(['/po-warehouse', '/warehouse-work-orders', '/warehouse-location', '/location-names'], 'Logistics'))} />,
      permission: ['purchasing', 'logistics', 'location'],
      subMenu: [
        { label: 'Purchase Orders', path: '/po-warehouse', permission: ['purchasing'] },
        { label: 'Work Orders', path: '/warehouse-work-orders', permission: ['logistics'] },
        { label: 'Locations', path: '/warehouse-location', permission: ['location'] }
      ]
    },
    {
      label: 'File History',
      icon: <FileText {...iconProps(locationPathname === '/file-upload-history')} />,
      path: '/file-upload-history',
      permission: ['fileHistory']
    },
    {
      label: 'Clients',
      icon: <Users {...iconProps(locationPathname === '/clients')} />,
      path: '/clients',
      permission: ['clients']
    },
    {
      label: 'Settings',
      icon: <SettingsIcon {...iconProps(isMenuActive(['/account', '/users'], 'Settings'))} />,
      permission: ['settings'],
      subMenu: [
        { label: 'Account', path: '/account', permission: ['settings'] },
        { label: 'Employee', path: '/employees', permission: ['settings'] }
      ]
    }
  ];

  const sellerPlusWarehouseMenu = [
    {
      label: 'Dashboard',
      icon: <LayoutDashboard {...iconProps(locationPathname === '/dashboard')} />,
      path: '/dashboard',
      permission: ['dashboard']
    },
    {
      label: 'Inventory',
      icon: <Package {...iconProps(isMenuActive(['/inventory/sku', '/inventory/products', '/inventory/vendor'], 'Inventory'))} />,
      permission: ['inventory'],
      subMenu: [
        { label: 'SKU', path: '/inventory/sku', permission: ['inventory'] },
        { label: 'Products', path: '/inventory/products', permission: ['inventory'] },
        { label: 'Vendor Items', path: '/inventory/vendor', permission: ['inventory'] }
      ]
    },
    {
      label: 'Purchasing',
      icon: <ShoppingCart {...iconProps(isMenuActive(['/vendors', '/po-history', '/restock'], 'Purchasing'))} />,
      permission: ['purchasing'],
      subMenu: [
        { label: 'Vendor CRM', path: '/vendors', permission: ['purchasing'] },
        { label: 'Purchase Orders', path: '/po-history', permission: ['purchasing'] },
        { label: 'Replenishment', path: '/restock', permission: ['purchasing'] }
      ]
    },
    {
      label: 'Sourcing',
      permission: ['catalog'],
      icon: <Search {...iconProps(isMenuActive(['/sourcing', '/database'], 'Sourcing'))} />,
      subMenu: [
        { label: 'Catalog Analyzer', path: '/sourcing', permission: ['catalog'] },
        { label: 'Database', path: '/database', permission: ['catalog'] }
      ]
    },
    {
      label: 'Logistics',
      icon: <Truck {...iconProps(isMenuActive(['/po-warehouse', '/warehouse-work-orders', '/warehouse-location', '/location-names'], 'Logistics'))} />,
      permission: ['location'],
      subMenu: [
        { label: 'Work Orders', path: '/work-orders', permission: ['logistics'] },
        { label: 'Locations', path: '/warehouse-location', permission: ['location'] }
      ]
    },
    {
      label: 'File History',
      icon: <FileText {...iconProps(locationPathname === '/file-upload-history')} />,
      path: '/file-upload-history',
      permission: ['fileHistory']
    },
    {
      label: 'Settings',
      icon: <SettingsIcon {...iconProps(isMenuActive(['/account', '/users'], 'Settings'))} />,
      permission: ['settings'],
      subMenu: [
        { label: 'Account', path: '/account', permission: ['settings'] },
        { label: 'Employee', path: '/employees', permission: ['settings'] },
        { label: 'Billing', path: '/billing', permission: ['settings'] }
      ]
    }
  ];

  const filteredUserMenu = userMenu.map((item) => {
    if (item.label === 'Settings') {
      return {
        ...item,
        subMenu: user?.id === 1 || user?.is_employee_of
          ? item.subMenu.filter(sub => sub.label !== 'Billing')
          : item.subMenu
      };
    }
    return item;
  });

  const filterSellerPlusWarehouseMenu = sellerPlusWarehouseMenu.map((item) => {
    if (item.label === 'Settings') {
      return {
        ...item,
        subMenu: user?.is_employee_of
          ? item.subMenu.filter(sub => sub.label !== 'Billing')
          : item.subMenu
      };
    }
    return item;
  });

  const getFilteredMenu = (menu) => {
    return menu.filter(item => {
      if (user?.id === 1) return true;
      if (user?.role?.id === 2 || user?.role?.id === 5) {
        if (item.label === 'Billing' && !isSubscribed) {
          return false;
        }
        return true;
      }
      return true;
    });
  };

  let menuData;
  if (user?.role?.name === 'seller+warehouse') {
    menuData = getFilteredMenu(filterSellerPlusWarehouseMenu);
  } else if (user?.role?.name === 'Warehouse') {
    menuData = getFilteredMenu(warehouseMenu);
  } else {
    menuData = getFilteredMenu(filteredUserMenu);
  }

  const permissionBasedSidebarFilter = menuData
    .map(item => {
      if (!user?.is_employee_of || (!item.permission && !item.subMenu?.some(sub => sub.permission))) {
        return item;
      }

      const getPermissionKey = (value) => {
        const key = Object.keys(PERMISSION_MAP).find(
          (key) => PERMISSION_MAP[key] === value
        );
        return key ? parseInt(key, 10) : null;
      };

      const hasPermission = (permissionName) => {
        const permissionKey = getPermissionKey(permissionName);
        return user?.permissions?.includes(permissionKey);
      };

      if (item.subMenu) {
        const filteredSubMenu = item.subMenu.filter(subItem => {
          return !subItem.permission || subItem.permission.some(perm => hasPermission(perm));
        });
        if (filteredSubMenu.length > 0 || !item.permission) {
          return { ...item, subMenu: filteredSubMenu };
        }
        return null;
      }

      if (item.permission) {
        const allowed = item.permission.some(perm => hasPermission(perm));
        return allowed ? item : null;
      }

      return null;
    })
    .filter(Boolean);

  const isSubscribed =
    currentActiveSubscription?.status === 'active';

  const toggleSubMenu = (label) => {
    setOpenSubMenu(prevState => (prevState === label ? null : label));
  };

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const [logoutHover, setLogoutHover] = useState(false);

  const UserPopoverContent = () => (
    <div style={{ padding: '12px 16px', minWidth: 200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.5px',
          borderRadius: '50%', minWidth: 36, width: 36, height: 36,
          background: '#1565D8', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {abbreviatedName}
        </div>
        <div style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Tooltip title={profileUser?.full_name}>
            <span style={{
              fontSize: 14, fontWeight: 600, color: '#1B2559',
              whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden',
              textOverflow: 'ellipsis', display: 'block', lineHeight: 1.4
            }}>
              {profileUser?.full_name}
            </span>
          </Tooltip>
          <Tooltip title={profileUser?.email}>
            <span style={{
              fontSize: 12, color: '#86909C',
              whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden',
              textOverflow: 'ellipsis', display: 'block', lineHeight: 1.4
            }}>
              {profileUser?.email}
            </span>
          </Tooltip>
          {profileUser?.company?.name && (
            <span style={{ fontSize: 12, color: '#4E5969', lineHeight: 1.4 }}>
              {profileUser?.company?.name}
            </span>
          )}
        </div>
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #F0F2F5', margin: '10px 0' }} />
      <div
        onClick={() => dispatch(SignOut())}
        onMouseEnter={() => setLogoutHover(true)}
        onMouseLeave={() => setLogoutHover(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          cursor: 'pointer', padding: '6px 0'
        }}
      >
        <FiLogOut style={{ fontSize: 14, color: logoutHover ? '#1565D8' : '#4E5969', transition: 'color 0.15s' }} />
        <span style={{ fontSize: 14, color: logoutHover ? '#1565D8' : '#4E5969', fontWeight: 400, transition: 'color 0.15s' }}>
          Sign out
        </span>
      </div>
    </div>
  );

  return (
    <SidebarContainer collapsed={collapsed}>
      <div className="sidebar-logo">
        {!collapsed && (
          <span
            className="logo-text"
            onClick={() => navigate(user?.role?.name === 'Warehouse' ? '/warehouse-location' : '/inventory/sku')}
          >
            Market<span className="logo-text-accent">OS</span>
          </span>
        )}
        <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <IoChevronForward /> : <HiOutlineMenuAlt2 />}
        </button>
      </div>
      <div className="sidebar-menu-area">
        <ProSidebar collapsed={collapsed}>
          <Menu iconShape="circle">
            {permissionBasedSidebarFilter.map((menu, index) => {
              const isBilling = menu.path === '/billing' || menu.label === 'Billing';
              const isEmployee = user?.is_employee_of;
              const isDisabled = user?.id === 1 ? false : (user?.role?.id === 2 || user?.role?.id === 5) && !isSubscribed && !isBilling && !isEmployee;
              return (
              menu.subMenu ? (
                <SubMenu
                  key={index}
                  title={menu.label}
                  icon={menu.icon}
                  open={openSubMenu === menu.label}
                  onClick={() => {
                    if (!isDisabled) toggleSubMenu(menu.label);
                  }}
                  className={`${openSubMenu === menu.label ? 'active' : ''}`}
                >
                  {menu.subMenu.map((item, subIndex) => (
                    <MenuItem
                      key={`${index}-${subIndex}`}
                      className={`submenu-item ${item.path?.startsWith(location.pathname) ? 'active' : ''}`}
                      disabled={isDisabled}
                      onClick={() => {
                        if (!isDisabled) {
                          handleMenuClick(item.path);
                        }
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </SubMenu>
              ) : (
                <MenuItem
                  key={index}
                  icon={menu.icon}
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      handleMenuClick(menu.path);
                    }
                  }}
                  className={`${location.pathname === menu.path ? 'active' : ''}`}
                >
                  {menu.label}
                </MenuItem>
              )
            );})}
          </Menu>
        </ProSidebar>
      </div>
      <Popover trigger="click" placement="topLeft" content={<UserPopoverContent />} overlayClassName="dropdown-pop">
        <div className="sidebar-profile">
          <div className="profile-avatar">{abbreviatedName}</div>
          {!collapsed && (
            <div className="profile-info">
              <span className="profile-name">{profileUser?.full_name}</span>
              <span className="profile-email">{profileUser?.email}</span>
            </div>
          )}
        </div>
      </Popover>
    </SidebarContainer>
  );
};

export default Sidebar;
